import { useTranslation } from 'react-i18next';
import { useTenantWritable } from '@/features/auth/useTenantWritable';

/**
 * The pay-to-restore banner shown to a SUSPENDED (unpaid) tenant (slice 11b).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 A SUSPENDED CUSTOMER STILL SEES THEIR FLEET. This banner is the ONLY thing
 * that changes about the view: reads succeed, the map, vehicles, trips, reports
 * and alerts render as normal, and every write affordance is hidden (not
 * disabled). The banner explains why the controls are gone and how to restore
 * them.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Rendered INSIDE the shell's measured chrome block (see AppShell `--raad-chrome`)
 * rather than as a floating overlay, so it PUSHES the route down instead of
 * covering the live-map controls — it works at 360px because it is a full-width
 * bar that wraps, never an absolutely-positioned card over the map.
 *
 * Renders nothing for a writable tenant, so it is not a dead control.
 */
export function SuspendedBanner() {
  const { t } = useTranslation();
  const writable = useTenantWritable();

  if (writable) return null;

  return (
    <div
      role="status"
      data-testid="suspended-banner"
      className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-raad-warn/40 bg-raad-warn/15 px-4 py-2 text-[12.5px] leading-relaxed text-raad-text sm:px-6"
    >
      <span className="font-bold text-raad-warn">{t('suspended.bannerTitle')}</span>
      <span className="text-raad-muted">{t('suspended.banner')}</span>
    </div>
  );
}
